import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Package,
  Search,
  Plus,
  Minus,
  Trash2,
  MapPin,
  Clock,
  User,
  AlertCircle,
  CheckCircle,
  Loader2,
  ChevronRight,
  Box,
  History,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { resolveTenantAdminId } from '../../lib/tenantStorage';
import { getAdminId } from '../../utils/getAdminId';
import {
  getBinContents,
  getBinLocationAddress,
  searchAllProductsWithBinInfo,
  assignProductToBin,
  updateBinInventoryQuantity,
  removeProductFromBin,
  getBinMovementHistory,
  transferProductBetweenBins,
} from '../../lib/services/binInventoryService';
import type {
  BinContentsItem,
  BinLocationAddress,
  BinMovementHistoryWithDetails,
} from '../../types/warehouse';
import type { ProductWithBinInfo } from '../../lib/services/binInventoryService';

interface BinAssignmentModalProps {
  binId: string;
  warehouseId: string;
  onClose: () => void;
  onInventoryChanged?: () => void;
}

type TabType = 'contents' | 'assign' | 'history';

export const BinAssignmentModal: React.FC<BinAssignmentModalProps> = ({
  binId,
  warehouseId,
  onClose,
  onInventoryChanged,
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('contents');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [location, setLocation] = useState<BinLocationAddress | null>(null);
  const [contents, setContents] = useState<BinContentsItem[]>([]);
  const [history, setHistory] = useState<BinMovementHistoryWithDetails[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<ProductWithBinInfo[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<{
    id: string;
    name: string;
    sku: string;
    quantity: number;
    current_bin: {
      bin_id: string;
      bin_code: string;
      bin_inventory_id: string;
      assigned_quantity: number;
    } | null;
  } | null>(null);
  const [assignQuantity, setAssignQuantity] = useState(1);
  const [assignNotes, setAssignNotes] = useState('');

  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editQuantity, setEditQuantity] = useState(0);

  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const loadData = useCallback(async () => {
    const adminId = resolveTenantAdminId(user, null);
    if (!adminId) return;
    setLoading(true);
    try {
      const [locationData, contentsData] = await Promise.all([
        getBinLocationAddress(binId),
        getBinContents(binId, adminId),
      ]);
      setLocation(locationData);
      setContents(contentsData);
    } catch (err) {
      console.error('Error loading bin data:', err);
    } finally {
      setLoading(false);
    }
  }, [binId, user]);

  const loadHistory = useCallback(async () => {
    const adminId = getAdminId(user);
    if (!adminId) return;
    const historyData = await getBinMovementHistory(adminId, { binId, limit: 50 });
    setHistory(historyData);
  }, [binId, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, loadHistory]);

  useEffect(() => {
    const search = async () => {
      const adminId = getAdminId(user);
      if (!adminId) return;
      setSearchLoading(true);
      try {
        const results = await searchAllProductsWithBinInfo(adminId, searchTerm, binId);
        setSearchResults(results);
      } finally {
        setSearchLoading(false);
      }
    };

    const debounce = setTimeout(search, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm, user, binId]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleAssignProduct = async () => {
    if (!selectedProduct || !user) return;
    const adminId = getAdminId(user);
    if (!adminId) return;

    setSaving(true);
    try {
      if (selectedProduct.current_bin) {
        const result = await transferProductBetweenBins(
          {
            product_id: selectedProduct.id,
            from_bin_id: selectedProduct.current_bin.bin_id,
            to_bin_id: binId,
            warehouse_id: warehouseId,
            quantity: assignQuantity,
            reason: assignNotes || `Transferred from ${selectedProduct.current_bin.bin_code}`,
          },
          user.id
        );

        if (result.success) {
          showNotification('success', `${selectedProduct.name} transferred from ${selectedProduct.current_bin.bin_code}`);
          setSelectedProduct(null);
          setAssignQuantity(1);
          setAssignNotes('');
          setSearchTerm('');
          setActiveTab('contents');
          await loadData();
          onInventoryChanged?.();
        } else {
          showNotification('error', result.error || 'Failed to transfer product');
        }
      } else {
        const result = await assignProductToBin(
          {
            bin_id: binId,
            product_id: selectedProduct.id,
            warehouse_id: warehouseId,
            quantity: assignQuantity,
            notes: assignNotes || undefined,
          },
          adminId,
          user.id
        );

        if (result.success) {
          showNotification('success', `${selectedProduct.name} assigned to bin`);
          setSelectedProduct(null);
          setAssignQuantity(1);
          setAssignNotes('');
          setSearchTerm('');
          setActiveTab('contents');
          await loadData();
          onInventoryChanged?.();
        } else {
          showNotification('error', result.error || 'Failed to assign product');
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateQuantity = async (item: BinContentsItem) => {
    if (!user) return;

    setSaving(true);
    try {
      const result = await updateBinInventoryQuantity(
        item.bin_inventory_id,
        editQuantity,
        user.id
      );

      if (result.success) {
        showNotification('success', 'Quantity updated');
        setEditingItem(null);
        await loadData();
        onInventoryChanged?.();
      } else {
        showNotification('error', result.error || 'Failed to update quantity');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveProduct = async (item: BinContentsItem) => {
    if (!confirm(`Remove ${item.product_name} from this bin?`)) return;

    setSaving(true);
    try {
      const result = await removeProductFromBin(item.bin_inventory_id);

      if (result.success) {
        showNotification('success', `${item.product_name} removed from bin`);
        await loadData();
        onInventoryChanged?.();
      } else {
        showNotification('error', result.error || 'Failed to remove product');
      }
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMovementTypeLabel = (type: string) => {
    switch (type) {
      case 'assign':
        return 'Assigned';
      case 'transfer':
        return 'Transferred';
      case 'remove':
        return 'Removed';
      case 'adjust':
        return 'Adjusted';
      default:
        return type;
    }
  };

  const getMovementTypeColor = (type: string) => {
    switch (type) {
      case 'assign':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30';
      case 'transfer':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30';
      case 'remove':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30';
      case 'adjust':
        return 'text-amber-600 bg-amber-100 dark:text-amber-400 dark:bg-amber-900/30';
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="absolute inset-y-0 right-0 w-full max-w-xl flex">
        <div className="relative w-full bg-white dark:bg-gray-900 shadow-xl flex flex-col">
          {notification && (
            <div
              className={`absolute top-4 left-4 right-4 z-10 px-4 py-3 rounded-lg flex items-center gap-2 ${
                notification.type === 'success'
                  ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200'
                  : 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200'
              }`}
            >
              {notification.type === 'success' ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              <span className="text-sm font-medium">{notification.message}</span>
            </div>
          )}

          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                  <Box className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {loading ? 'Loading...' : location?.bin_code || 'Bin'}
                  </h2>
                  {location && (
                    <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                      <MapPin className="h-3.5 w-3.5" />
                      <span>{location.full_address}</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <button
                onClick={() => setActiveTab('contents')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'contents'
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Package className="h-4 w-4 inline-block mr-1.5" />
                Contents ({contents.length})
              </button>
              <button
                onClick={() => setActiveTab('assign')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'assign'
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Plus className="h-4 w-4 inline-block mr-1.5" />
                Assign Item
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'history'
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <History className="h-4 w-4 inline-block mr-1.5" />
                History
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
              </div>
            ) : activeTab === 'contents' ? (
              <div className="p-6">
                {contents.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">
                      This bin is empty
                    </p>
                    <Button
                      variant="primary"
                      size="sm"
                      className="mt-4"
                      onClick={() => setActiveTab('assign')}
                    >
                      <Plus className="h-4 w-4 mr-1.5" />
                      Assign First Item
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {contents.map((item) => (
                      <div
                        key={item.bin_inventory_id}
                        className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                              {item.product_name}
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              SKU: {item.product_sku}
                              {item.product_barcode && (
                                <span className="ml-2">
                                  Barcode: {item.product_barcode}
                                </span>
                              )}
                            </p>
                          </div>
                          <button
                            onClick={() => handleRemoveProduct(item)}
                            disabled={saving}
                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                            title="Remove from bin"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            {editingItem === item.bin_inventory_id ? (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => setEditQuantity(Math.max(0, editQuantity - 1))}
                                  className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                                <input
                                  type="number"
                                  value={editQuantity}
                                  onChange={(e) => setEditQuantity(Math.max(0, parseInt(e.target.value) || 0))}
                                  className="w-20 px-3 py-1.5 text-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                />
                                <button
                                  onClick={() => setEditQuantity(editQuantity + 1)}
                                  className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleUpdateQuantity(item)}
                                  disabled={saving}
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => setEditingItem(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <div
                                onClick={() => {
                                  setEditingItem(item.bin_inventory_id);
                                  setEditQuantity(item.quantity);
                                }}
                                className="px-3 py-1.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg font-medium cursor-pointer hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors"
                              >
                                Qty: {item.quantity}
                              </div>
                            )}
                            <span className="text-xs text-gray-400">
                              of {item.total_product_quantity} total
                            </span>
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            <span>{formatDate(item.assigned_at)}</span>
                          </div>
                          {item.assigned_by_name && (
                            <div className="flex items-center gap-1">
                              <User className="h-3.5 w-3.5" />
                              <span>{item.assigned_by_name}</span>
                            </div>
                          )}
                        </div>

                        {item.notes && (
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                            {item.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : activeTab === 'assign' ? (
              <div className="p-6">
                {selectedProduct ? (
                  <div className="space-y-4">
                    {selectedProduct.current_bin ? (
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <RefreshCw className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                              Transfer Product
                            </span>
                          </div>
                          <button
                            onClick={() => setSelectedProduct(null)}
                            className="p-1.5 rounded-lg text-gray-500 hover:bg-blue-100 dark:hover:bg-blue-800"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-gray-100">
                            {selectedProduct.name}
                          </h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            SKU: {selectedProduct.sku}
                          </p>
                        </div>
                        <div className="mt-3 flex items-center gap-2 text-sm">
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-mono">
                            {selectedProduct.current_bin.bin_code}
                          </span>
                          <ArrowRight className="h-4 w-4 text-blue-500" />
                          <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 rounded text-emerald-700 dark:text-emerald-300 font-mono">
                            {location?.bin_code || 'This bin'}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-blue-600 dark:text-blue-400">
                          Currently assigned: {selectedProduct.current_bin.assigned_quantity} units
                        </p>
                      </div>
                    ) : (
                      <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-gray-100">
                              {selectedProduct.name}
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              SKU: {selectedProduct.sku}
                            </p>
                          </div>
                          <button
                            onClick={() => setSelectedProduct(null)}
                            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <p className="mt-2 text-sm text-emerald-600 dark:text-emerald-400">
                          Available stock: {selectedProduct.quantity}
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {selectedProduct.current_bin ? 'Quantity to transfer' : 'Quantity to assign'}
                      </label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setAssignQuantity(Math.max(1, assignQuantity - 1))}
                          className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
                        >
                          <Minus className="h-5 w-5" />
                        </button>
                        <input
                          type="number"
                          value={assignQuantity}
                          onChange={(e) => {
                            const maxQty = selectedProduct.current_bin
                              ? selectedProduct.current_bin.assigned_quantity
                              : selectedProduct.quantity;
                            setAssignQuantity(Math.max(1, Math.min(maxQty, parseInt(e.target.value) || 1)));
                          }}
                          className="w-24 px-4 py-2 text-center text-lg font-medium border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        />
                        <button
                          onClick={() => {
                            const maxQty = selectedProduct.current_bin
                              ? selectedProduct.current_bin.assigned_quantity
                              : selectedProduct.quantity;
                            setAssignQuantity(Math.min(maxQty, assignQuantity + 1));
                          }}
                          className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
                        >
                          <Plus className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            const maxQty = selectedProduct.current_bin
                              ? selectedProduct.current_bin.assigned_quantity
                              : selectedProduct.quantity;
                            setAssignQuantity(maxQty);
                          }}
                          className="px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg"
                        >
                          All
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {selectedProduct.current_bin ? 'Transfer reason (optional)' : 'Notes (optional)'}
                      </label>
                      <textarea
                        value={assignNotes}
                        onChange={(e) => setAssignNotes(e.target.value)}
                        placeholder={selectedProduct.current_bin ? 'Add reason for transfer...' : 'Add notes about this assignment...'}
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400"
                      />
                    </div>

                    <Button
                      variant={selectedProduct.current_bin ? 'secondary' : 'primary'}
                      className={`w-full ${selectedProduct.current_bin ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600' : ''}`}
                      onClick={handleAssignProduct}
                      disabled={saving || assignQuantity < 1}
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : selectedProduct.current_bin ? (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      {selectedProduct.current_bin ? 'Transfer to This Bin' : 'Assign to Bin'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by name, SKU, or barcode..."
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        autoFocus
                      />
                    </div>

                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Search all products - transfer from other bins or assign unassigned items
                    </p>

                    {searchLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                      </div>
                    ) : searchResults.length === 0 ? (
                      <div className="text-center py-8">
                        <Package className="h-10 w-10 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                        <p className="text-gray-500 dark:text-gray-400">
                          {searchTerm
                            ? 'No products found matching your search'
                            : 'No products available'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {searchResults.map((product) => (
                          <button
                            key={product.id}
                            onClick={() => {
                              setSelectedProduct({
                                id: product.id,
                                name: product.name,
                                sku: product.sku,
                                quantity: product.quantity,
                                current_bin: product.current_bin,
                              });
                              const maxQty = product.current_bin
                                ? product.current_bin.assigned_quantity
                                : product.quantity;
                              setAssignQuantity(Math.min(maxQty, 1));
                            }}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${
                              product.current_bin
                                ? 'bg-blue-50 dark:bg-blue-900/10 hover:bg-blue-100 dark:hover:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                                : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              product.current_bin
                                ? 'bg-blue-200 dark:bg-blue-800'
                                : 'bg-gray-200 dark:bg-gray-700'
                            }`}>
                              {product.image_url ? (
                                <img
                                  src={product.image_url}
                                  alt={product.name}
                                  className="w-full h-full object-cover rounded-lg"
                                />
                              ) : (
                                <Package className={`h-5 w-5 ${product.current_bin ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {product.name}
                                </p>
                                {product.current_bin ? (
                                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                    In {product.current_bin.bin_code}
                                  </span>
                                ) : (
                                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded">
                                    Unassigned
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {product.sku} | Stock: {product.quantity}
                                {product.current_bin && (
                                  <span className="ml-1 text-blue-600 dark:text-blue-400">
                                    ({product.current_bin.assigned_quantity} in bin)
                                  </span>
                                )}
                              </p>
                            </div>
                            {product.current_bin ? (
                              <RefreshCw className="h-5 w-5 text-blue-500 flex-shrink-0" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6">
                {history.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">
                      No movement history for this bin
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {history.map((record) => (
                      <div
                        key={record.id}
                        className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded ${getMovementTypeColor(
                                record.movement_type
                              )}`}
                            >
                              {getMovementTypeLabel(record.movement_type)}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(record.created_at)}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            x{record.quantity}
                          </span>
                        </div>

                        <p className="mt-2 font-medium text-gray-900 dark:text-gray-100">
                          {record.product?.name || 'Unknown Product'}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {record.product?.sku}
                        </p>

                        {record.movement_type === 'transfer' && (
                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                            {record.from_bin?.bin_code || 'Unknown'} <ChevronRight className="h-4 w-4 inline" />{' '}
                            {record.to_bin?.bin_code || 'Unknown'}
                          </p>
                        )}

                        {record.reason && (
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                            {record.reason}
                          </p>
                        )}

                        {record.performed_by_user && (
                          <p className="mt-2 text-xs text-gray-400 flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {record.performed_by_user.full_name}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
